import React, { useMemo } from 'react';
import { formatUtcClock, formatUtcTime } from '../../lib/time';

export interface TimeScrubberMarker {
  at: number;
  kind: 'incident';
}

export interface TimeScrubberProps {
  min: number;
  max: number;
  value: number;
  onChange: (ms: number) => void;
  markers?: TimeScrubberMarker[];
  /** When false, the control still moves but the parent should not filter the graph. */
  filtersActive?: boolean;
  className?: string;
}

const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
/** Marks closer than this share of the rail keep their tick, but only the first one writes its hour. */
export const MIN_LABEL_GAP_PCT = 10;

export function parseIsoMs(iso?: string): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

export function defaultTimelineWindow(nowMs = Date.now()): { min: number; max: number } {
  return { min: nowMs - 24 * HOUR_MS, max: nowMs };
}

export function windowFromTimes(times: number[]): { min: number; max: number } {
  const valid = times.filter((ms) => Number.isFinite(ms));
  if (valid.length === 0) return defaultTimelineWindow();
  const min = Math.min(...valid);
  let max = Math.max(...valid);
  if (max - min < MINUTE_MS) max = min + HOUR_MS;
  return { min, max };
}

function clamp(ms: number, min: number, max: number): number {
  if (max <= min) return min;
  return Math.min(max, Math.max(min, ms));
}

function formatClock(ms: number): string {
  return formatUtcTime(ms);
}

/** An axis tick: the zone is written once, in the scrubber's own label, not on every tick. */
function formatHour(ms: number): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '';
  return formatUtcClock(date);
}

function formatT0Offset(ms: number, t0: number): string {
  const delta = Math.max(0, ms - t0);
  const totalMins = Math.floor(delta / MINUTE_MS);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  const secs = Math.floor((delta % MINUTE_MS) / 1000);
  if (hours > 0) {
    return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function mergeMarkers(markers: TimeScrubberMarker[]): TimeScrubberMarker[] {
  const sorted = [...markers]
    .filter((marker) => Number.isFinite(marker.at))
    .sort((a, b) => a.at - b.at);
  const out: TimeScrubberMarker[] = [];
  for (const marker of sorted) {
    const prev = out[out.length - 1];
    if (prev && Math.abs(marker.at - prev.at) < MINUTE_MS) continue;
    out.push(marker);
  }
  return out;
}

const TimeScrubber: React.FC<TimeScrubberProps> = ({
  min,
  max,
  value,
  onChange,
  markers = [],
  filtersActive = true,
  className = '',
}) => {
  const span = Math.max(1, max - min);
  const t0 = min;
  const current = clamp(value, min, max);
  const progress = ((current - min) / span) * 100;
  const ticks = useMemo(() => {
    let lastLabelled = -Infinity;
    return mergeMarkers(markers).map((marker) => {
      const left = ((marker.at - min) / span) * 100;
      const labelled = left - lastLabelled >= MIN_LABEL_GAP_PCT;
      if (labelled) lastLabelled = left;
      return { marker, left, labelled };
    });
  }, [markers, min, span]);

  return (
    <div className={`c3-timescrub${filtersActive ? '' : ' is-idle'}${className ? ` ${className}` : ''}`}>
      <div className="c3-timescrub-meta">
        <span className="c3-timescrub-k">T0</span>
        <span className="c3-timescrub-v">{formatClock(t0)}</span>
      </div>
      <div className="c3-timescrub-track">
        <div className="c3-timescrub-rail">
          <div className="c3-timescrub-fill" style={{ width: `${progress}%` }} />
          {ticks.map(({ marker, left, labelled }) => (
            <div
              key={`${marker.kind}-${marker.at}`}
              className="c3-timescrub-mark"
              style={{ left: `${left}%` }}
            >
              <span className={`c3-timescrub-label${labelled ? '' : ' is-hidden'}`}>{formatHour(marker.at)}</span>
              <span className="c3-timescrub-bar" title="Incident" />
            </div>
          ))}
        </div>
        <input
          type="range"
          className="c3-timescrub-input"
          min={min}
          max={max}
          step={Math.max(1, Math.round(span / 1000))}
          value={current}
          aria-label="Investigation time"
          onChange={(e) => onChange(Number(e.target.value))}
        />
      </div>
      <div className="c3-timescrub-readout">
        T = {formatClock(current)}
        <span className="c3-timescrub-offset"> · T0 + {formatT0Offset(current, t0)}</span>
      </div>
    </div>
  );
};

export default TimeScrubber;
